import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map, scan, shareReplay, startWith } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { MultiselectComponent } from '@ui-lib/components';
import { WorkerStatusDictionaryService } from './worker-status-dictionary.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mon-worker-status-selector',
  standalone: true,
  imports: [AsyncPipe, TranslateModule, FormsModule, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './worker-status-selector.component.html',
  styleUrl: './worker-status-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkerStatusSelectorComponent implements OnInit {
  protected readonly workerStatusDictionaryService = inject(WorkerStatusDictionaryService);
  private readonly destroyRef = inject(DestroyRef);

  @Input()
  public controlEntity: FormControl<number[]> = new FormControl([], { nonNullable: true });

  @Output()
  public readonly selectionChange: EventEmitter<IDictionaryItem<number>[]> = new EventEmitter<
    IDictionaryItem<number>[]
  >();

  public ngOnInit(): void {
    combineLatest([
      this.workerStatuses$,
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([items, selectedIds]) => {
        const selectedSet = new Set(selectedIds ?? []);
        const selectedItems = items.filter(
          (item) => item.id !== undefined && selectedSet.has(item.id),
        );

        this.selectionChange.emit(selectedItems);
      });
  }

  protected readonly workerStatuses$ = this.workerStatusDictionaryService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<number>[],
        newbies: IDictionaryItem<number>[],
      ): IDictionaryItem<number>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<number>[]): IDictionaryItem<number>[] => {
      const seenIds = new Set<number>();

      return items.filter((item: IDictionaryItem<number>): boolean => {
        if (item.id === undefined) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
}
